import { Component, ElementRef } from '@angular/core';
import { max, range, ScaleBand, scaleBand, select } from 'd3';
import {
  BarChartComponent,
  selectPartialUnderlines
} from '../../../../tranche/components/tranche-summary/bar-chart/bar-chart.component';
import {
  BarGroupData,
  BarX
} from '../../../../shared/models/charts/chart-data-types';

// tslint:disable:restrict-plus-operands -- too many violations using w3c and other stuff

@Component({
  providers: [],
  templateUrl: './selection-bar-chart.template.html',
  selector: 'muni-gameday-selection-bar-chart',
  standalone: false,
})
export class SelectionBarChartComponent extends BarChartComponent {
  protected extraElementCfg = {
    extendBarWidthOn: 50,
    multibarsGroupHeaderHeight: 50,
    singlebarGroupHeaderHeight: 25,
    topLabelBottomMargin: 10,
    underlineBottomMargin: 12,
    selectionLineHeight: 3,
    crossSignDimension: 15,
    headerItemsMargin: 7,
    selectionUnderlineColor: '#F78C1E'
  };

  private hoverX: ScaleBand<any> = scaleBand<any>();

  protected setNewAxisDomain() {
    if (this.data && this.data.groups.length) {
      this._x.domain(this.data.groups.map((d) => d.x));
      this.hoverX.domain(this.data.groups.map((d) => d.x));
      this.y.domain([
        0,
        max(
          this.data.groups.map((d) =>
            d.bars.reduce(
              (acc, el) => Math.max(acc, el.parts[el.parts.length - 1].y),
              0
            )
          )
        )
      ]);
    }
  }

  constructor(element: ElementRef) {
    super(element);
  }

  public render() {
    this.setNewAxisBands();
    super.render();
    this.drawHoverAreas();
    this.handleSelection();
  }

  protected bindData() {
    const barGroups = this.chart.selectAll('.bar-group').data(this.data.groups);
    barGroups.enter().append('g').attr('class', 'bar-group');
    barGroups.classed('expanded', (d) => d.expanded);
    barGroups.classed(
      'bar-group-period-closed',
      (groupData: BarGroupData) => !!groupData.orderPeriodClosed
    );
    barGroups.classed('multibars', (d) => d.barsCount > 1);
    barGroups.exit().remove();

    const selectionLayer = this.chart
      .selectAll('.bar-group')
      .selectAll('.selection-layer')
      .data((d) => [d]);
    selectionLayer.enter().append('g').attr('class', 'selection-layer');
    selectionLayer.exit().remove();

    this.render();
  }

  protected getXCorrection(d: BarX): number {
    const expandedGroup = this.getExpandedGroup();
    let correction = 0;
    let expandedGroupWidth = 0;
    if (expandedGroup) {
      expandedGroupWidth =
        this.extraElementCfg.extendBarWidthOn * expandedGroup.barsCount;
      correction = d.idx > expandedGroup.x.idx ? expandedGroupWidth : 0;
    }
    return correction;
  }

  protected getWidthCorrection(d: BarX): number {
    const expandedGroup = this.getExpandedGroup();
    let expandedGroupWidth = 0;
    let extraWidth = 0;

    if (expandedGroup) {
      expandedGroupWidth =
        this.extraElementCfg.extendBarWidthOn * expandedGroup.barsCount;
      extraWidth = d.idx === expandedGroup.x.idx ? expandedGroupWidth : 0;
    }
    return extraWidth;
  }

  protected x(d?: BarX): any {
    if (d) {
      return this._x(d) + this.getXCorrection(d);
    } else {
      return this._x;
    }
  }

  protected getExpandedGroup(): BarGroupData {
    return this.data.groups.filter((g) => g.expanded)[0];
  }

  protected getScale(d: any): number {
    const expandedGroup = this.getExpandedGroup();
    let scale = 0.2;

    if (expandedGroup) {
      scale = d.x.idx === expandedGroup.x.idx ? 0.3 : 0.2;
    }
    return scale;
  }

  protected subX(d: any): ScaleBand<any> {
    const scale = this.getScale(d);
    return this._subX
      .domain(range(d.barsCount || 1).map((el) => el.toString()))
      .range([
        this.x(d.x),
        this.x(d.x) + this.x().bandwidth() + this.getWidthCorrection(d.x)
      ])
      .padding(scale);
  }

  protected subHoverX(d: any): ScaleBand<any> {
    const scale = this.getScale(d);
    return this._subX
      .domain(range(d.barsCount || 1).map((el) => el.toString()))
      .range([
        this.x(d.x),
        this.x(d.x) + this.x().bandwidth() + this.getWidthCorrection(d.x)
      ])
      .padding(0)
      .paddingOuter(scale / 2);
  }

  protected height(value: number): number {
    return this.cfg.svg.actualHeight - this.y(value);
  }

  protected setNewAxisBands() {
    const padding = 0.2;
    let correction = 0;
    if (this.data && this.data.groups.length) {
      const expandedGroup = this.getExpandedGroup();
      correction =
        this.extraElementCfg.extendBarWidthOn *
        (expandedGroup ? expandedGroup.barsCount : 0);
    }
    const width = this.cfg.svg.actualWidth - correction;
    this._x.rangeRound([0, width]).padding(padding);
    this.y.rangeRound([
      this.cfg.svg.actualHeight,
      this.elementCfg.xAxisHeight +
        this.elementCfg.topLabelBottomMargin +
        this.elementCfg.underlineBottomMargin +
        this.extraElementCfg.topLabelBottomMargin +
        this.extraElementCfg.underlineBottomMargin
    ]);
    this.hoverX
      .rangeRound([0, width])
      .padding(0)
      .paddingOuter(padding / 2);
  }

  protected handleSelection() {
    this._drawSelectionBackground();
    this._drawCrossSign();
    this._drawSelectionUnderline();
    this._transformExistingElements();
  }

  protected _drawSelectionBackground(): void {
    const y = 0;
    const height = this.cfg.svg.actualHeight + this.cfg.margin.bottom + 5; // Add five to make sure it touches the bottom
    const width = (d) =>
      this.x().bandwidth() +
      this.extraElementCfg.extendBarWidthOn * d.barsCount;
    const background = this.chart
      .selectAll('.selection-layer')
      .selectAll('.selected-background')
      .data((d: any) => (d.expanded ? [d] : []));

    background
      .enter()
      .append('rect')
      .attr('class', 'selected-background')
      .attr('x', (d) => this.x(d.x))
      .attr('y', height)
      .attr('width', width)
      .attr('height', y)
      .attr('fill', 'white')
      .transition()
      .duration(this.cfg.duration)
      .attr('height', height)
      .attr('y', y);

    background
      .transition()
      .duration(this.cfg.duration)
      .attr('x', (d) => this.x(d.x))
      .attr('width', width)
      .attr('height', height)
      .attr('y', 0);

    background.exit().remove();
  }

  protected _drawCrossSign() {
    const y = this.extraElementCfg.crossSignDimension;
    const x = (d) =>
      this.x(d.x) +
      this.x().bandwidth() +
      this.extraElementCfg.extendBarWidthOn * d.barsCount -
      this.extraElementCfg.crossSignDimension;

    const crossSign = this.chart
      .selectAll('.selection-layer')
      .selectAll('.cross-sign')
      .data((d: any) => (d.expanded ? [d] : []));

    crossSign
      .enter()
      .append('text')
      .attr('class', 'cross-sign')
      .attr('x', x)
      .attr('y', y)
      .text((d) => this.icons.times)
      .on('click', (event: any) => this.emitEvent('cross-click', undefined));

    crossSign.transition().duration(this.cfg.duration).attr('x', x);

    crossSign.exit().remove();
  }

  protected _transformExistingElements() {
    const width = (d) =>
      this.x().bandwidth() +
      this.extraElementCfg.extendBarWidthOn * d.barsCount;

    const underline = this.chart
      .selectAll('.bar-group')
      .selectAll('.underline')
      .data((d) => [d]);
    underline
      .filter((d: any) => d.expanded)
      .transition()
      .duration(this.cfg.duration)
      .attr('x', (d: any) => this.x(d.x))
      .attr(
        'y',
        this.extraElementCfg.headerItemsMargin +
          this.elementCfg.topLabelBottomMargin
      )
      .attr('width', width);

    const x = function (d) {
      return this.x(d.x) + width(d) / 2;
    }.bind(this);
    const topLabel = this.chart
      .selectAll('.bar-group')
      .select('.top-label')
      .filter((d: any) => d.expanded);
    topLabel
      .transition()
      .duration(this.cfg.duration)
      .attr('x', x)
      .attr(
        'y',
        this.extraElementCfg.headerItemsMargin +
          this.elementCfg.underlineBottomMargin
      );

    const bottomLabel = this.chart
      .selectAll('.bar-group')
      .select('.bottom-label')
      .filter((d: any) => d.expanded);
    bottomLabel
      .transition()
      .duration(this.cfg.duration)
      .attr('x', x)
      .attr('y', this.y(0) + this.elementCfg.xAxisHeight)
      .each(function (data, index) {
        select(this).selectAll('.sub-label').attr('x', x(data));
      });

    const partialUnderlines = this.chart
      .selectAll('.bar-group')
      .selectAll('.bar-group > .partial-underline')
      .data((d) => selectPartialUnderlines(d).filter((item) => !item.expanded));

    partialUnderlines.exit().remove();
  }

  protected _drawSelectionUnderline() {
    const getX = (d) => this.subX(d.parts[d.parts.length - 1]);
    const x = (d) => getX(d)(d.barIdx);
    const width = (d) => getX(d).bandwidth();
    const selection = this.chart
      .selectAll('.selection-layer')
      .selectAll('.selection')
      .data((d: any) => d.bars.filter((el) => el.selected));
    selection
      .enter()
      .append('rect')
      .attr('class', 'selection')
      .attr('x', x)
      .attr('y', this.cfg.svg.actualHeight + this.cfg.margin.bottom)
      .attr('height', this.extraElementCfg.selectionLineHeight)
      .attr('width', width)
      .attr('fill', this.extraElementCfg.selectionUnderlineColor);

    selection
      .transition()
      .duration(this.cfg.duration)
      .attr('x', x)
      .attr('width', width)
      .attr(
        'y',
        this.cfg.svg.actualHeight +
          this.elementCfg.xAxisHeight +
          this.extraElementCfg.underlineBottomMargin
      );

    selection.exit().remove();
  }

  protected drawTopLabels() {
    super.drawTopLabels();

    this._drawTopLabels(
      this.chart
        .selectAll('.stacked-bar')
        .selectAll('.stacked-bar > .top-label')
        .data((d) => [d]),
      (d) =>
        this.subX(d.parts[d.parts.length - 1])(d.barIdx || 0) +
        this.subX(d.parts[d.parts.length - 1]).bandwidth() / 2,
      (d) =>
        this.y(d.parts[d.parts.length - 1].y) -
        this.elementCfg.topLabelBottomMargin +
        4,
      (d) => d.topLabel
    );
  }

  protected drawBottomLabels() {
    super.drawBottomLabels();

    this._drawBottomLabels(
      this.chart
        .selectAll('.stacked-bar')
        .selectAll('.stacked-bar > .bottom-label')
        .data((d: any) => (d.barsCount > 1 ? [d] : [])),
      (d) =>
        this.subX(d.parts[d.parts.length - 1])(d.barIdx || 0) +
        this.subX(d.parts[d.parts.length - 1]).bandwidth() / 2,
      (d) => this.y(0) + this.elementCfg.xAxisHeight,
      (d) => d.bottomLabel
    );
  }

  protected drawHoverAreas(): void {
    this.drawHoverGroupAreas();
    this.drawHoverBarsAreas();
  }

  protected drawHoverGroupAreas(): void {
    const bandwidth = this.hoverX.bandwidth();
    const x = (d) => (this.hoverX(d.x) || 0) + this.getXCorrection(d.x);
    const y = 0;
    const width = (d) => bandwidth + this.getWidthCorrection(d.x);
    const height = this.cfg.svg.actualHeight + this.cfg.margin.bottom;
    const clickCb = (d) => this.emitEvent('bar-click', d.bars[0].parts[0].id);
    const combineHandler = (d) =>
      this.emitEvent('combine-click', this.getCombined(d.x.value));
    const hoverGroupAreas = this.chart
      .selectAll('.bar-group')
      .selectAll('.bar-group > g.hovered-area')
      .data((d) => [d]);
    this._drawHoverGroupArea(
      hoverGroupAreas,
      x,
      y,
      width,
      height,
      clickCb,
      combineHandler
    );
  }

  public getCombined(id: string): string[] {
    const combined = this.data.groups
        .filter((d) => d.combined)
        .map((d) => d.x.value),
      curItemDate = id,
      idx = combined.indexOf(curItemDate);
    if (idx !== -1) {
      combined.splice(idx, 1);
    } else {
      combined.push(curItemDate);
    }
    return combined;
  }

  protected drawHoverBarsAreas(): void {
    const x = (d) => this.subHoverX(d.parts[0])(d.barIdx || 0);
    const y =
      this.elementCfg.topLabelBottomMargin +
      this.extraElementCfg.topLabelBottomMargin +
      this.extraElementCfg.underlineBottomMargin;
    const width = (d) =>
      this.subHoverX(d.parts[d.parts.length - 1]).bandwidth();
    const height = this.cfg.svg.actualHeight + this.cfg.margin.bottom - y;
    const hoverBarsAreas = this.chart
      .selectAll('.stacked-bar')
      .selectAll('.hover-area')
      .data((d) => [d]);
    const clickCb = (d) => this.emitEvent('bar-click', d.parts[0].id);

    this._drawHoverArea(hoverBarsAreas, x, y, width, height, clickCb);
  }

  protected _drawHoverArea(selection, x, y, width, height, clickCb): void {
    selection
      .enter()
      .insert('rect', 'g.stacked-bar')
      .attr('class', 'hover-area hovered-area') // second class 'hovered-area' - just for automation testers
      .attr('x', x)
      .attr('rx', 5)
      .attr('ry', 5)
      .attr('width', width)
      .attr('y', y)
      .attr('height', height)
      .on('click', clickCb);

    selection
      .attr('x', x)
      .attr('width', width)
      .attr('y', y)
      .attr('height', height);

    selection.exit().remove();
  }

  protected _drawHoverGroupArea(
    selection,
    x,
    y,
    width,
    height,
    clickCb,
    combineHandler
  ): void {
    const radius = (val) => (width(val) > 10 ? 10 : width(val) / 1.5);
    const showRule = (d) => !d.expanded && (d.combined || d.barsCount > 1);
    const sign = (d) => (d.combined ? this.icons.unlink : this.icons.link);
    selection
      .enter()
      .insert('g', 'g.stacked-bar')
      .attr('class', 'hovered-area');

    selection.attr('class', (d) => {
      return `hovered-area${d.highlighted ? ' highlighted' : ''}`;
    });

    selection.exit().remove();

    // removing previous state elements
    this.chart
      .selectAll('.bar-group')
      .selectAll('.bar-group > g.hovered-area')
      .selectAll('rect')
      .remove();

    // // re-drawing rect after combining
    this.chart
      .selectAll('.bar-group')
      .selectAll('.bar-group > g.hovered-area')
      .selectAll('rect')
      .data((d) => [d])
      .enter()
      .insert('rect')
      .attr('class', 'hover-area')
      .attr('data-automation-e2e-selector', 'rect')
      .attr('rx', 5)
      .attr('ry', 5)
      .attr('x', x)
      .attr('width', width)
      .attr('y', y)
      .attr('height', height)
      .on('click', clickCb);

    this.chart
      .selectAll('.bar-group')
      .selectAll('.bar-group > g.hovered-area')
      .selectAll('circle')
      .remove();

    // creating
    this.chart
      .selectAll('.bar-group')
      .selectAll('.bar-group > g.hovered-area')
      .selectAll('circle')
      .data((d) => [d])
      .enter()
      .append('circle')
      .attr('class', 'hover-area')
      .attr('style', (d) => (showRule(d) ? '' : 'display: none;'))
      .attr('r', radius)
      .attr('cx', (val) => x(val) + width(val) / 2)
      .attr('cy', y + 20)
      .on('click', combineHandler);

    // removing previous state elements
    this.chart
      .selectAll('.bar-group')
      .selectAll('.bar-group > g.hovered-area')
      .selectAll('text')
      .remove();

    // re-creating combine symbol
    this.chart
      .selectAll('.bar-group')
      .selectAll('.bar-group > g.hovered-area')
      .selectAll('text')
      .data((d) => [d])
      .enter()
      .append('text')
      .attr('class', 'hover-area')
      .attr('class', 'combine-split-onhover')
      .attr('style', (d) => (showRule(d) ? '' : 'display: none;'))
      .attr('x', (val) => x(val) + width(val) / 2)
      .attr('y', y + 24.5)
      .on('click', combineHandler)
      .text(sign);
  }
}
